/*
  Add All to Cart for the wishlist. One implementation, shared by the wishlist
  page (assets/wishlist.js) and the wishlist drawer (assets/wishlist-slideout.js).

  Shopify adds an `items` batch all-or-nothing: one sold-out variant fails the
  whole request with a 422 and adds nothing. Every earlier version of this
  feature (2022, the Black Friday 2025 hot edit, the July 2026 drawer change)
  ignored the status and removed the whole list from Swym anyway, so a single
  item selling out after the page loaded emptied the shopper's wishlist and
  left them on an empty cart. Here only the variants Shopify reports it added
  are removed from the wishlist.

  When a batch is refused, stock is re-read once for the whole list and the
  sold-out items dropped before the rest is retried in one request. Only if
  Shopify still refuses part of it is the batch split in halves. An earlier
  version retried one item at a time: 20 items with one sold out took 24s and
  21 cart writes, enough to trip Shopify's cart rate limit on Black Friday.

  The wishlist removals are deliberately not awaited. Waiting on the wishlist
  app before showing the cart is what made Add All crawl at the Black Friday
  2025 launch (hence the old "allow up to a minute" copy). The cart now opens
  in the drawer instead of navigating away, so the removals finish in the
  background. Where it does still navigate (the cart page reloading, or a page
  with no drawer), it gives the removals a few seconds to land first, since
  leaving the page cancels them and the added items would stay saved.
  Removals go through MNWishlistStore.removeByVariant (assets/wishlist-store.js).

  Loaded by both sections/wishlist.liquid and sections/wishlist-slideout.liquid,
  so it must stay safe to run twice.
*/
(() => {
  if (window.MNWishlist && window.MNWishlist.addAllToCart) return;

  const CART_SECTIONS = ['cart-drawer', 'cart-icon-bubble'];
  const STOCK_CHECK_BATCH = 50;
  // Longest a navigation waits on the wishlist removals.
  const LEAVE_WAIT_MS = 4000;

  // Wishlist removals still in flight, from every Add All on this page view.
  let pendingRemoval = Promise.resolve();
  // Set when an add changed the cart behind the cart page, which has no drawer
  // to show it in, so the page needs a reload.
  let cartPageStale = false;

  function cartUrl() {
    return (window.routes && window.routes.cart_url) || '/cart';
  }

  function onCartPage() {
    return window.location.pathname === cartUrl();
  }

  // THROTTLED / NON_JSON come from global.js parseCartResponse: Shopify's rate
  // limit or bot challenge, i.e. a busy store rather than a bad connection.
  function isBusy(err) {
    return Boolean(err) && (err.code === 'THROTTLED' || err.code === 'NON_JSON');
  }

  function addUrl() {
    return (window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add') + '.js';
  }

  // POST /cart/add.js. global.js's fetchCartJSON retries a throttled request
  // (Shopify rejects those before touching the cart, so a retry never doubles).
  async function postAdd(variantIds, withSections) {
    const body = { items: variantIds.map((id) => ({ id: Number(id), quantity: 1 })) };
    if (withSections) {
      body.sections = CART_SECTIONS;
      body.sections_url = window.location.pathname;
    }
    const config = {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    };
    if (typeof window.fetchCartJSON === 'function') return window.fetchCartJSON(addUrl(), config);
    const response = await fetch(addUrl(), config);
    return { response, json: await response.json() };
  }

  // The first add, as postAdd. A dropped connection can lose Shopify's answer
  // after it added the items: the 19 Oct 2026 stress test showed "Nothing was
  // added" over a cart holding all 10, and trying again doubles them. The
  // batch is all-or-nothing, so when the cart now holds every one of the
  // variants the add went through, and this answers as Shopify would have.
  // (All of them already being in the cart looks the same, but then they're in
  // the cart either way, which is what Add All is for.)
  async function postFirstAdd(variantIds) {
    try {
      return await postAdd(variantIds, true);
    } catch (err) {
      if (isBusy(err)) throw err;
      const items = await linesInCart(variantIds).catch(() => null);
      if (!items) throw err;
      const sections = await fetchCartSections().catch(() => null);
      return { response: { ok: true, status: 200 }, json: { items, sections } };
    }
  }

  // The cart's lines for variantIds when it holds every one of them, else null.
  async function linesInCart(variantIds) {
    const response = await fetch(`${cartUrl()}.js`, { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) return null;
    const cart = await response.json();
    const wanted = new Set(variantIds.map(String));
    const lines = (cart.items || []).filter((line) => wanted.has(String(line.variant_id)));
    return new Set(lines.map((line) => String(line.variant_id))).size === wanted.size ? lines : null;
  }

  function succeeded({ response, json }) {
    return response.ok && json && !json.status;
  }

  function errorText(json) {
    return (json && (json.description || json.message)) || '';
  }

  async function fetchCartSections() {
    const response = await fetch(`${rootUrl()}?sections=${CART_SECTIONS.join(',')}`);
    return response.ok ? response.json() : null;
  }

  // Remove the given variant ids from the shopper's wishlist. Resolves when the
  // wishlist app has answered for every one (or failed); never rejects.
  function removeFromWishlist(variantIds) {
    const store = window.MNWishlistStore;
    if (!store || !variantIds.length) return Promise.resolve();
    return store.removeByVariant(variantIds).catch((e) => console.error('Wishlist: remove failed', e));
  }

  function rootUrl() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  // Live stock for the given entries as Map(variantId -> available), read from the
  // card section the wishlist drawer renders (sections/wishlist-slideout-card.liquid),
  // one request per 50 products. Null when there are no product ids to look up.
  async function liveStock(entries) {
    const productIds = [...new Set(entries.map((e) => String(e.pid || '')).filter(Boolean))];
    if (!productIds.length) return null;
    const batches = [];
    for (let i = 0; i < productIds.length; i += STOCK_CHECK_BATCH) batches.push(productIds.slice(i, i + STOCK_CHECK_BATCH));
    const pages = await Promise.all(
      batches.map(async (ids) => {
        const params = new URLSearchParams({
          section_id: 'wishlist-slideout-card',
          type: 'product',
          q: ids.map((id) => `id:${id}`).join(' OR '),
          'options[unavailable_products]': 'show',
          'options[prefix]': 'none',
        });
        const response = await fetch(`${rootUrl()}search?${params}`);
        return response.ok ? response.text() : '';
      })
    );
    const stock = new Map();
    pages.forEach((html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      doc.querySelectorAll('[data-wishlist-variants]').forEach((el) => {
        try {
          JSON.parse(el.textContent).forEach((v) => stock.set(String(v.id), Boolean(v.available)));
        } catch (e) {
          // A card we can't read just isn't pre-filtered; the add decides.
        }
      });
    });
    return stock;
  }

  // Add entries; when Shopify refuses the batch, split it in halves and try each,
  // so a refusal costs a few extra requests instead of one per item. Halves go
  // one after the other: parallel writes to one cart contend for the same lock.
  //
  // A request that never got Shopify's answer (throttled, a bot challenge, a
  // dropped connection) says nothing about its items, so they go to
  // result.retry rather than result.failed: they stay buyable instead of being
  // shown as sold out. The rest of the split stops there too, since more writes
  // won't get past a store that is pushing back.
  async function addSplitting(entries, result, state, withSections) {
    if (result.retryError) {
      result.retry.push(...entries);
      return;
    }
    let response;
    try {
      response = await postAdd(
        entries.map((e) => e.epi),
        withSections
      );
    } catch (err) {
      result.retryError = err;
      result.retry.push(...entries);
      return;
    }
    if (succeeded(response)) {
      state.items.push(...(response.json.items || []));
      if (withSections) state.sections = response.json.sections;
      return;
    }
    if (response.response.status !== 422 || entries.length === 1) {
      entries.forEach((e) => result.failed.push({ ...e, message: errorText(response.json) }));
      return;
    }
    const mid = Math.ceil(entries.length / 2);
    await addSplitting(entries.slice(0, mid), result, state, false);
    await addSplitting(entries.slice(mid), result, state, false);
  }

  /*
    entries: [{ epi, pid, label }]: epi is the saved variant id, pid its product
    id (used to re-check stock), label names it for messages
    ("Essential Hoodie - Sandstorm, XS").

    options.removeFromWishlist (default true): false leaves added items saved,
    for a list shared with the shopper, which isn't theirs to change.

    Resolves to { added, failed, retry, retryError, error, cart } and never rejects:
      added      entries Shopify added (and that are now being removed from the wishlist)
      failed     entries Shopify refused, each with Shopify's message
      retry      entries not added because a retry request was throttled or lost
                 partway through; still saved, and worth trying again
      retryError the error that stopped those, for the message
      error      set when the first request itself failed and the cart doesn't hold
                 the items, in which case nothing was added
      cart       { sections, items } for cart-drawer renderContents, when anything was added
  */
  async function addAllToCart(entries, { removeFromWishlist: remove = true } = {}) {
    const seen = new Set();
    const unique = (entries || []).filter((entry) => {
      const key = String(entry.epi || '');
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    const result = { added: [], failed: [], retry: [], retryError: null, error: null, cart: null };
    if (!unique.length) return result;

    const state = { items: [], sections: null };
    let batch;
    try {
      batch = await postFirstAdd(unique.map((e) => e.epi));
    } catch (err) {
      result.error = err;
      return result;
    }

    if (succeeded(batch)) {
      state.items = batch.json.items || [];
      state.sections = batch.json.sections;
    } else if (batch.response.status === 422) {
      // Usually something sold out since the page loaded. Re-read stock once,
      // drop what's gone, and add the rest.
      let toAdd = unique;
      const stock = await liveStock(unique).catch(() => null);
      if (stock) {
        toAdd = unique.filter((e) => stock.get(String(e.epi)) !== false);
        unique
          .filter((e) => stock.get(String(e.epi)) === false)
          .forEach((e) => result.failed.push({ ...e, message: 'Sold out' }));
      }
      if (toAdd.length < unique.length) {
        if (toAdd.length) await addSplitting(toAdd, result, state, true);
      } else if (unique.length === 1) {
        result.failed.push({ ...unique[0], message: errorText(batch.json) });
      } else {
        // Stock doesn't explain the refusal (a quantity limit, say), so the same
        // batch would fail again: go straight to halves.
        const mid = Math.ceil(unique.length / 2);
        await addSplitting(unique.slice(0, mid), result, state, false);
        await addSplitting(unique.slice(mid), result, state, false);
      }
      if (state.items.length && !state.sections) state.sections = await fetchCartSections().catch(() => null);
    } else {
      result.error = new Error(errorText(batch.json) || `Add to cart failed (${batch.response.status})`);
      return result;
    }

    // Only what Shopify says it added leaves the wishlist. Anything it didn't
    // mention stays saved; it isn't reported as a failure either, since the
    // request as a whole succeeded.
    const addedIds = new Set(state.items.map((item) => String(item.variant_id || item.id)));
    result.added = unique.filter((e) => addedIds.has(String(e.epi)));
    if (state.items.length) result.cart = { sections: state.sections, items: state.items };

    if (result.added.length) {
      if (typeof window.invalidateCartCache === 'function') window.invalidateCartCache();
      // Auto-gift, auto-donation and the cart count sync, as after every other add.
      if (typeof window.runPostAddChores === 'function') window.runPostAddChores();
      if (remove) {
        const epis = result.added.map((e) => e.epi);
        pendingRemoval = Promise.all([pendingRemoval, removeFromWishlist(epis)]);
        window.dispatchEvent(new CustomEvent('wishlist:items-removed', { detail: { epis } }));
      }
    }
    return result;
  }

  // Navigating cancels wishlist requests still in flight, which would leave the
  // added items saved in the wishlist as well as in the cart. Let the removals
  // land first, but never hold the shopper on the wishlist app for long.
  function leavePage(go) {
    const timeout = new Promise((resolve) => setTimeout(resolve, LEAVE_WAIT_MS));
    Promise.race([pendingRemoval, timeout]).then(go);
  }

  // Put the freshly added items in the cart drawer, opening it unless told not to.
  // The cart page has no drawer, so it reloads instead: straight away when
  // opening, otherwise once the shopper is done with the message (viewCart, or
  // refreshCartPage when the wishlist drawer closes). Falls back to the cart
  // page when this page has no drawer to render into.
  function showCart(cart, { open = true } = {}) {
    if (onCartPage()) {
      cartPageStale = true;
      if (open) refreshCartPage();
      return;
    }
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.renderContents === 'function' && cart && cart.sections) {
      cartDrawer.renderContents(cart, open);
      return;
    }
    if (open) {
      leavePage(() => {
        window.location.href = cartUrl();
      });
    }
  }

  // Reloads the cart page when an add changed the cart behind it; otherwise does nothing.
  function refreshCartPage() {
    if (!cartPageStale || !onCartPage()) return;
    cartPageStale = false;
    leavePage(() => window.location.reload());
  }

  // The "View cart" link in a result message. cart:open (cart-drawer.js) re-reads
  // the cart, so a line the post-add chores added since (auto-donation) shows too.
  function viewCart() {
    if (onCartPage()) {
      refreshCartPage();
      return;
    }
    document.dispatchEvent(new CustomEvent('cart:open'));
  }

  function names(list) {
    const shown = list.slice(0, 3).map((e) => e.label).filter(Boolean);
    if (!shown.length) return '';
    const more = list.length - shown.length;
    return ` (${shown.join('; ')}${more > 0 ? `; and ${more} more` : ''})`;
  }

  // The message to show for a result, or '' when everything went in.
  function describe(result) {
    const added = result.added.length;
    const failed = result.failed.length;
    const retry = (result.retry || []).length;
    if (result.error) {
      return isBusy(result.error)
        ? "The store is very busy right now, so nothing was added and your wishlist hasn't changed. Please try again in a moment."
        : "Nothing was added to your cart. Please check your connection and try again.";
    }
    if (!failed && !retry) return '';
    const itemWord = (n) => (n === 1 ? 'item' : 'items');
    if (!added && !retry) {
      return failed === 1
        ? `This couldn't be added to your cart${names(result.failed)}. It may have sold out since you saved it, so it's still in your wishlist.`
        : `These couldn't be added to your cart${names(result.failed)}. They may have sold out since you saved them, so they're still in your wishlist.`;
    }
    const sentences = [];
    if (added) sentences.push(`Added ${added} ${itemWord(added)} to your cart.`);
    if (failed) {
      sentences.push(
        `${failed} ${itemWord(failed)} couldn't be added${names(result.failed)} and ${failed === 1 ? 'is' : 'are'} still in your wishlist.`
      );
    }
    if (retry) {
      const cause = isBusy(result.retryError) ? 'The store got very busy' : 'The connection dropped';
      sentences.push(
        `${cause}, so ${retry} ${itemWord(retry)} ${retry === 1 ? "wasn't" : "weren't"} added yet. ${
          retry === 1 ? "It's" : "They're"
        } still in your wishlist, so please try again in a moment.`
      );
    }
    return sentences.join(' ');
  }

  window.MNWishlist = Object.assign(window.MNWishlist || {}, {
    addAllToCart,
    showCart,
    viewCart,
    refreshCartPage,
    describe,
    removeFromWishlist,
  });
})();
